import { Avatar, Box, Chip, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { ELeaveDaysStatus } from '../enums/leave-days/ELeaveDays';
import { formatDate } from '../functions/formatDate';
import {
  getLeaveDayTypeColor,
  getVacationStatusColor,
} from '../functions/getChipColors';
import { getUserInitials } from '../functions/getUserInitials';
import { ILeaveDay } from '../interfaces/leave-days/ILeaveDay';
import { darkTheme, lightTheme } from '../mui/theme';

export const useLeaveDaysColumns = (
  setSelectedItem: (leave: ILeaveDay) => void,
  setShowDeleteModal: (show: boolean) => void,
  setIsAddModalOpened: (show: boolean) => void,
  hideActionColumn = false,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('general.user'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            style={{ marginRight: '8px' }}
            src={params.row?.user?.profilePhoto?.thumbnail}
          >
            {getUserInitials(params.row?.user)}
          </Avatar>
          {params?.row?.user?.displayName}
        </div>
      ),
    },
    {
      field: 'dates',
      headerName: t('general.dates'),
      width: 250,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { startDate, endDate } = params?.row;
        return (
          <div>
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        );
      },
    },
    {
      field: 'leaveDayType',
      headerName: t('general.leaveDayType'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`leaveDays.leaveDayType.${params.value}`)}
          color={getLeaveDayTypeColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`leaveDays.status.${params.value}`)}
          color={getVacationStatusColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      sortable: false,
      headerClassName: hideActionColumn ? 'hideColumn' : '',
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <Box
            sx={{
              display: hideActionColumn ? 'none' : 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {/* Edit or View Icon */}
            {params?.row?.status === ELeaveDaysStatus.PENDING && (
              <Tooltip title={t('general.edit')}>
                <span
                  className={'d-flex'}
                  onClick={() => {
                    setSelectedItem(params?.row);
                    setIsAddModalOpened(true);
                  }}
                >
                  <EditIcon
                    fill={
                      userData?.theme === ETheme.LIGHT
                        ? lightTheme.palette.text.primary
                        : darkTheme.palette.text.primary
                    }
                  />
                </span>
              </Tooltip>
            )}

            {/* Delete Icon */}
            {(params?.row?.status === ELeaveDaysStatus.PENDING ||
              !isNormalUser) && (
              <Tooltip title={t('general.delete')}>
                <span
                  className={'ms-3 d-flex'}
                  onClick={() => {
                    setSelectedItem(params?.row);
                    setShowDeleteModal(true);
                  }}
                >
                  <TrashIcon
                    fill={
                      userData?.theme === ETheme.LIGHT
                        ? lightTheme.palette.text.primary
                        : darkTheme.palette.text.primary
                    }
                  />
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];
};
